import { useContext } from 'react'

import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCustomItems from '@hooks/useCustomItems'
import useCustomItemGroup, { useCustomItemGroupForm } from '@hooks/useCustomItemGroup'

import { confirmDelete, formatEntityCount } from '@functions/entity'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditCustomItemGroupModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCustomItemGroup: (payload: HandleCallbackActionParams) => void,
  updateCustomItemGroup: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCustomItemGroupModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCustomItemGroup, updateCustomItemGroup } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const customItemGroup = digObject(modalPayload, 'customItemGroup', {})

  const newRecord = !customItemGroup.id

  const {
    callbacks: {
      createCustomItemGroup: createFn,
      deleteCustomItemGroup: deleteFn,
      updateCustomItemGroup: updateFn,
    },
    creating,
    deleting,
    updating,
  } = useCustomItemGroup(customItemGroup)

  const {
    entityState,
    entityState: {
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = useCustomItemGroupForm(customItemGroup, { validateOn: 'change' })

  const { filteredCustomItemsCount } = useCustomItems({ filters: { custom_item_group_id: customItemGroup.id } })

  const formattedEntityCount = formatEntityCount({ entityString: 'Item', entityCount: filteredCustomItemsCount })

  const createOrUpdateCustomItemGroupPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Group ${newRecord ? 'Created' : 'Updated'}`,
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: {
      confirmString: `This cannot be reversed${filteredCustomItemsCount ? ` and will remove ${formattedEntityCount}` : ''}.`,
      deleteString: 'remove',
      entityName: 'Group',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Group" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          {!newRecord && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                // @ts-expect-error TS2345
                onClick={() => confirmDelete(confirmDeletePayload)}
                size="medium"
              >
                Delete Group
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCustomItemGroup(createOrUpdateCustomItemGroupPayload)
              : () => updateCustomItemGroup(createOrUpdateCustomItemGroupPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Group` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCustomItemGroupModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
